import React, { useState, useEffect, useRef, useCallback } from 'react'
import { indexStudentPagination } from '../../Api'
import { GetClass } from '../../Api'
import { useNavigate } from 'react-router-dom'

import {
    Container,
    List,
    Emp,
    EmpMain,
    Avatar,
    Span,
    StatusBadge,
    Search,
    DivNewEmp,
    FormFilter,
    FormSearch,
    AreaEmp,
    InputEmp,
    Btt02,
    AddBtn,
    Select,
    ContainerDivs,
    SkeletonRow,
    LoadMoreText,
    EmptyState,
} from './style';

/* iniciais para o avatar */
const initials = (name) =>
    (name || '').trim().split(/\s+/).slice(0, 2).map(n => n[0] || '').join('') || '?';

const PAGE_SIZE = 20;

const Student = () => {

    const navigate = useNavigate()
    const currentYear = new Date().getFullYear().toString();

    const [student, setStudent] = useState([])      // alunos JÁ carregados (acumulados)
    const [total, setTotal] = useState(0)           // total no servidor (para saber se há mais)
    const [page, setPage] = useState(1)

    const [positionAtEducationDepartment, setPositionAtEducationDepartment] = useState('')
    const [Clss, setClss] = useState([])
    const [busca, setBusca] = useState("")
    const [filter, setFilter] = useState("")
    // Status do aluno usado para a contabilidade. Começa em "ativo"
    // para que o total reflita SOMENTE os alunos ativos.
    const [status, setStatus] = useState("")

    const [loading, setLoading] = useState(true)        // 1ª carga (tela cheia)
    const [searching, setSearching] = useState(false)   // busca/filtro (NÃO desmonta o input)
    const [loadingMore, setLoadingMore] = useState(false) // cargas seguintes (inline)

    // idSchool fica guardado pra reuso nas próximas páginas
    const idSchoolRef = useRef(null)
    // Evita corrida: ignora respostas de buscas antigas
    const requestIdRef = useRef(0)
    // Assinatura do filtro atual: descarta resposta cujo filtro mudou
    // no meio do caminho (impede misturar páginas de filtros diferentes)
    const queryKeyRef = useRef("")

    // hasMore vem do SERVIDOR (página cheia = pode ter mais), não de
    // student.length < total — que quebra se vier algum duplicado.
    const [hasMore, setHasMore] = useState(false)

    // ───────────────────────────────────────────────────────────────
    // Busca uma página no servidor.
    // replace = true  → substitui a lista (1ª carga, nova busca/filtro)
    // replace = false → adiciona ao final (scroll / "carregar mais")
    //
    // statusToLoad → filtra a contabilidade por status no SERVIDOR.
    //   Vazio ("") = todos os status. "ativo" = só ativos.
    // ───────────────────────────────────────────────────────────────
    const fetchStudents = useCallback(async (pageToLoad, replace, search, idClass, statusToLoad) => {
        const idSchool = idSchoolRef.current;
        if (idSchool == null) return;

        const myKey = JSON.stringify({
            search: search || "",
            idClass: idClass || "",
            status: statusToLoad || ""
        });

        // numa nova busca/filtro, esta passa a ser a consulta "vigente"
        if (replace) {
            queryKeyRef.current = myKey;
            setHasMore(false);   // trava o observer até a nova lista chegar
            setSearching(true);
        } else {
            // append só é válido se o filtro não mudou desde então
            if (queryKeyRef.current !== myKey) return;
            setLoadingMore(true);
        }

        const reqId = ++requestIdRef.current;

        try {
            const response = await indexStudentPagination({
                idSchool,
                page: pageToLoad,
                limit: PAGE_SIZE,
                search: search || "",
                idClass: idClass || "",
                status: statusToLoad || ""
            });

            // resposta antiga (já houve request mais novo) → descarta
            if (reqId !== requestIdRef.current) return;
            // o filtro mudou enquanto esta resposta voltava → descarta
            if (queryKeyRef.current !== myKey) return;

            const data = response.data?.data || [];
            const totalCount = response.data?.total ?? data.length;
            setTotal(totalCount);
            setPage(pageToLoad);

            setStudent(prev => {
                if (replace) {
                    // só há mais se a página veio cheia E ainda não cobriu o total
                    setHasMore(data.length === PAGE_SIZE && data.length < totalCount);
                    return data;
                }

                // append: deduplica por _id (blinda contra repetição do backend)
                const seen = new Set(prev.map(s => s._id));
                const novos = data.filter(s => !seen.has(s._id));
                const merged = [...prev, ...novos];

                // só continua se: veio página cheia, houve progresso real
                // (algum aluno novo) e ainda não alcançou o total.
                const aindaTem =
                    data.length === PAGE_SIZE &&
                    novos.length > 0 &&
                    merged.length < totalCount;

                setHasMore(aindaTem);
                return merged;
            });
        } catch (err) {
            console.error("Erro ao carregar alunos:", err);
        } finally {
            if (reqId === requestIdRef.current) {
                setSearching(false);
                setLoadingMore(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Carga inicial: turmas + 1ª página de alunos (em paralelo) ──
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const idSchool = JSON.parse(sessionStorage.getItem("id-school"));
            idSchoolRef.current = idSchool;

            const positionAtEducationDepartment =
                localStorage.getItem("positionAtEducationDepartment");

            const [resClass] = await Promise.all([
                GetClass(idSchool),
                // 1ª carga usa o MESMO status do estado (coerência total)
                fetchStudents(1, true, "", "", status)
            ]);

            if (cancelled) return;

            const Year = resClass.data.data.filter(y => y.year === currentYear);
            setClss(Year);
            setPositionAtEducationDepartment(positionAtEducationDepartment);
            setLoading(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentYear]);

    // ── Busca/Filtro/Status: debounce de 400ms e refaz a partir da página 1 ──
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const t = setTimeout(() => {
            fetchStudents(1, true, busca, filter, status);
        }, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [busca, filter, status]);

    const loadMore = useCallback(() => {
        if (loadingMore || searching || !hasMore) return;
        fetchStudents(page + 1, false, busca, filter, status);
    }, [loadingMore, searching, hasMore, page, busca, filter, status, fetchStudents]);

    // ── Auto-carregar ao rolar (IntersectionObserver) ──
    const sentinelRef = useRef(null);
    useEffect(() => {
        if (!hasMore) return;
        const node = sentinelRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMore();
            },
            { rootMargin: "200px" }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loadMore]);

    const NewStudent = async () => {
        navigate('/new/student')
    }

    const StudentInformation = async (student) => {
        setLoading(true);
        navigate(`/student/info/${student._id}`)
        setLoading(false);
    }

    // Rótulo legível para o status selecionado
    const statusLabel = {
        "ativo": "Ativos",
        "transferido": "Transferidos",
        "inativo": "Inativos",
        "concluido": "Concluídos"
    }[status] ?? status;

    return (
        <Container>
            {loading ? (
                <ContainerDivs>
                    <Search>
                        <FormSearch>
                            <label>Buscar Aluno</label>
                            <AreaEmp><InputEmp type="text" placeholder="Buscar por nome" disabled /></AreaEmp>
                        </FormSearch>
                        <FormFilter>
                            <label>Filtrar por Turma</label>
                            <Select disabled><option>Todos</option></Select>
                        </FormFilter>
                        <FormFilter>
                            <label>Filtrar por Status</label>
                            <Select disabled><option>Ativos</option></Select>
                        </FormFilter>
                    </Search>
                    <List>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <SkeletonRow key={i}>
                                <span className="circle" />
                                <span className="bar" />
                                <span className="pill" />
                            </SkeletonRow>
                        ))}
                    </List>
                </ContainerDivs>
            ) : (
                <ContainerDivs>
                    <Search>
                        <FormSearch>
                            <label>Buscar Aluno</label>
                            <AreaEmp>
                                <InputEmp
                                    type="text"
                                    placeholder='Buscar por nome'
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                />
                            </AreaEmp>
                        </FormSearch>
                        <FormFilter>
                            <label>Filtrar por Turma</label>
                            <Select id="position"
                                value={filter || ""}
                                onChange={(e) => setFilter(e.target.value)}
                            >
                                <option value="">Todos</option>
                                {Clss.map(c => (
                                    <option key={c._id} value={c._id}>{c.name}</option>
                                ))}
                            </Select>
                        </FormFilter>
                        <FormFilter>
                            <label>Filtrar por Status</label>
                            <Select id="status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="ativo">Ativos</option>
                                <option value="transferido">Transferidos</option>
                                <option value="inativo">Inativos</option>
                                <option value="concluido">Concluídos</option>
                                <option value="">Todos</option>
                            </Select>
                        </FormFilter>
                    </Search>

                    <List>
                        {!positionAtEducationDepartment &&
                            <DivNewEmp>
                                <AddBtn onClick={NewStudent}>Novo Aluno</AddBtn>
                            </DivNewEmp>
                        }

                        <p>
                            Total de Alunos ({statusLabel}): <em>{total}</em>
                            {searching && ' — buscando...'}
                        </p>

                        {student.map(stu => {
                            const tone = stu.status || 'ativo';
                            const label = stu.status || 'ativo';
                            return (
                                <Emp onClick={() => StudentInformation(stu)} key={stu._id}>
                                    <EmpMain>
                                        <Avatar>{initials(stu.name)}</Avatar>
                                        <Span>{stu.name}</Span>
                                    </EmpMain>
                                    <StatusBadge $tone={tone}>{label}</StatusBadge>
                                </Emp>
                            );
                        })}

                        {!loadingMore && !searching && student.length === 0 && (
                            <EmptyState>
                                <span className="em">🔍</span>
                                Nenhum aluno encontrado.
                            </EmptyState>
                        )}

                        {/* Sentinela para auto-carregar ao rolar */}
                        {hasMore && <div ref={sentinelRef} style={{ height: "1px" }} />}

                        {loadingMore && <LoadMoreText>Carregando mais alunos...</LoadMoreText>}

                        {/* Fallback manual */}
                        {hasMore && !loadingMore && (
                            <DivNewEmp>
                                <Btt02 onClick={loadMore}>
                                    Carregar mais ({total - student.length} restantes)
                                </Btt02>
                            </DivNewEmp>
                        )}
                    </List>
                </ContainerDivs>
            )}
        </Container>
    )
}

export default Student